import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useLocation } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import { TemplateViewType } from "components/EditorHome/Constants";
import {
  $Avatar,
  $Col,
  $Drawer,
  $Empty,
  $Row,
  $Skeleton,
  $Tooltip,
  $Popconfirm,
  $Button,
  $Input,
  $MessageBox,
} from "us.common/components";
import { RootState } from "us.helper/types";
import { getUrlParams } from "components/EditorHome/Functions";
import {
  FileTextOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  BoxIcons,
  IconTypes,
} from "us.icons";
import * as Actions from "actions";
import { TextData } from "interfaces/TextEditor";
import { useTranslation } from "react-i18next";
import { TextEditor } from "components/TextEditor";
import { INITIAL_TEXT_OBJECT } from "constants/TextEditor";
import { useFormikContext } from "formik";
import { getGroupIdByGroupName, getTemplateGroupName } from "functions";

interface ITextList {
  templateView: any;
  navigateToTextEditor: (textObject: TextData) => void;
}

/**
 * @description - Text list component.
 * @link Design Document - https://unicorn-solutions.atlassian.net/l/cp/e59ify4K
 * @author Lashini Ayesha <lashinia@unicorn-solutions.com>
 * @since 12/09/2023
 */

const TemplateList: React.FC<ITextList & PropsFromRedux> = ({
  texts,
  templateView,
  drawer,
  templates,
  getAllTexts,
  setDrawerProperties,
  navigateToTextEditor,
  saveText,
  deleteText,
  getAdapterId,
}) => {
  const { search } = useLocation();
  const { t } = useTranslation();
  const { groups } = templates ?? [];
  const { groupId, categoryId } = getUrlParams(search);
  const textData = texts.data ?? [];
  const { values, setFieldValue } = useFormikContext<any>();
  const [selectedTextId, setSelectedTextId] = useState<string>("");

  const groupName = useMemo(() => {
    return getTemplateGroupName(templates.groups, groupId);
  }, [groupId, templates.groups]);

  useEffect(() => {
    getAllTexts && getAllTexts({ groupName });
  }, [groupName]);

  useEffect(() => {
    getAdapterId &&
      getAdapterId({
        categoryId: categoryId,
        isRepeating: false,
        groupId: getGroupIdByGroupName(groupName, groups),
      });
  }, [categoryId]);

  /**
   * @description - Handle save the changes to selected section
   */
  const handleSaveTextName = (modifiedTextData: TextData) => {
    try {
      setSelectedTextId("");
      saveText && saveText({ request: modifiedTextData, texts: textData });
    } catch (error) {
      console.error("Error saving the Section Editor", error);
      $MessageBox(
        "error",
        `US.USC.TEMPLATEEDITOR:MESSAGES.TEXT_UPDATE_FAILED`,
        "",
        ""
      );
    }
  };

  return (
    <div className="content-list-template">
      <$Skeleton
        loading={texts.isLoading}
        active
        paragraph={{
          rows: 3,
        }}
      >
        {templateView == TemplateViewType.GRID && (
          <$Row gutter={[12, 12]}>
            <$Col span={6}>
              <div className="template-item ti-create-new">
                <div
                  className="template-default-item py-3"
                  onClick={() => navigateToTextEditor(INITIAL_TEXT_OBJECT)}
                >
                  <h3 className="mb-1">
                    <PlusOutlined />
                  </h3>
                  <h3>
                    {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.CREATE_NEW_TEXT")}
                  </h3>
                </div>
              </div>
            </$Col>
            {textData.map((textObject: TextData) => {
              return (
                <$Col key={textObject.id} span={6}>
                  <div
                    className={`template-item py-3 ${
                      selectedTextId == textObject.id ? "ti-edit" : ""
                    }`}
                  >
                    <div className="template-item-title d-flex px-2 py-3">
                      <div className="d-flex w-100">
                        <div className="d-flex align-items-start">
                          <$Avatar
                            className="template-icon"
                            icon={<FileTextOutlined />}
                          />
                        </div>
                        {selectedTextId != textObject.id ? (
                          <div className="pl-2" style={{ paddingTop: 5 }}>
                            <span>{textObject?.textName}</span>
                          </div>
                        ) : (
                          <div className="pl-2 w-100">
                            <div className="d-flex align-items-center">
                              <div className="flex-grow-1">
                                <$Input
                                  name="displayName"
                                  size="small"
                                  className="w-100"
                                  value={textObject?.textName}
                                />
                              </div>
                              <div
                                className="d-flex align-items-center"
                                style={{ paddingTop: 2 }}
                              >
                                <$Tooltip
                                  placement="top"
                                  title={t("US.COLLECTION.COMMON:COMMON.SAVE")}
                                >
                                  <$Button
                                    type="primary"
                                    size="small"
                                    onClick={() => {
                                      handleSaveTextName({
                                        ...textObject,
                                        textName: values?.displayName,
                                      });
                                    }}
                                    className="ml-1"
                                    style={{ marginTop: "-3px" }}
                                  >
                                    <CheckOutlined />
                                  </$Button>
                                </$Tooltip>
                                <$Tooltip
                                  placement="top"
                                  title={t(
                                    "US.COLLECTION.COMMON:COMMON.CANCEL"
                                  )}
                                >
                                  <$Button
                                    size="small"
                                    onClick={() => setSelectedTextId("")}
                                    className="ml-1"
                                    style={{ marginTop: "-3px" }}
                                  >
                                    <CloseOutlined />
                                  </$Button>
                                </$Tooltip>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="template-item-options">
                      <$Tooltip
                        placement="top"
                        title={t("US.COLLECTION.COMMON:COMMON.EDIT")}
                      >
                        <$Button
                          size="small"
                          onClick={() => navigateToTextEditor(textObject)}
                          className="ml-1"
                        >
                          <EditOutlined />
                        </$Button>
                      </$Tooltip>
                      <$Tooltip
                        placement="top"
                        title={t(
                          "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.CHANGE_DISPLAY_NAME"
                        )}
                      >
                        <$Button
                          size="small"
                          onClick={() => {
                            setSelectedTextId(textObject?.id);
                            setFieldValue(
                              "displayName",
                              `${textObject.textName}`
                            );
                          }}
                          className="ml-1"
                        >
                          <BoxIcons type={IconTypes.BOX_ICON} name="rename" />
                        </$Button>
                      </$Tooltip>
                      <$Popconfirm
                        title={t(
                          "US.COLLECTION.CASE:MASSAGETEMPLATES.DELETECONFIRM"
                        )}
                        onConfirm={() =>
                          deleteText &&
                          deleteText({
                            request: {
                              textId: textObject.id,
                              textGroup: groupName,
                            },
                            texts: textData,
                          })
                        }
                        okText={t("US.COLLECTION.COMMON:COMMON.YES")}
                        cancelText={t("US.COLLECTION.COMMON:COMMON.NO")}
                      >
                        <$Tooltip
                          placement="top"
                          title={t("US.COLLECTION.COMMON:COMMON.DELETE")}
                        >
                          <$Button size="small" className="ml-1">
                            <DeleteOutlined className="text-error" />
                          </$Button>
                        </$Tooltip>
                      </$Popconfirm>
                    </div>
                  </div>
                </$Col>
              );
            })}
          </$Row>
        )}
        {templateView == TemplateViewType.LIST && (
          <>
            {textData.map((textObject: TextData) => {
              return (
                <div
                  key={textObject.id}
                  className={`template-list-item ${
                    selectedTextId == textObject.id ? "ti-edit" : ""
                  }`}
                >
                  <div className="template-list-item-row">
                    <div className="template-list-item-col">
                      <div className="d-flex align-items-center">
                        <div>
                          <$Avatar
                            className="template-icon"
                            icon={<FileTextOutlined />}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="template-list-item-col">
                      <div className="d-flex flex-column">
                        {selectedTextId != textObject.id ? (
                          <div className="font-weight-bold d-flex align-items-center">
                            <span>{textObject?.textName}</span>
                          </div>
                        ) : (
                          <div className="pl-2 w-100">
                            <div className="d-flex align-items-center">
                              <div className="flex-grow-1">
                                <$Input
                                  name="displayName"
                                  size="small"
                                  className="w-100"
                                  value={textObject?.textName}
                                />
                              </div>
                              <div
                                className="d-flex align-items-center"
                                style={{ paddingTop: 2 }}
                              >
                                <$Tooltip
                                  placement="top"
                                  title={t("US.COLLECTION.COMMON:COMMON.SAVE")}
                                >
                                  <$Button
                                    size="small"
                                    type="primary"
                                    onClick={() => {
                                      handleSaveTextName({
                                        ...textObject,
                                        textName: values?.displayName,
                                      });
                                    }}
                                    className="ml-1"
                                    style={{ marginTop: "-3px" }}
                                  >
                                    <CheckOutlined />
                                  </$Button>
                                </$Tooltip>
                                <$Tooltip
                                  placement="top"
                                  title={t(
                                    "US.COLLECTION.COMMON:COMMON.CANCEL"
                                  )}
                                >
                                  <$Button
                                    size="small"
                                    onClick={() => setSelectedTextId("")}
                                    className="ml-1"
                                    style={{ marginTop: "-3px" }}
                                  >
                                    <CloseOutlined />
                                  </$Button>
                                </$Tooltip>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="template-item-options">
                    <$Tooltip
                      placement="top"
                      title={t("US.COLLECTION.COMMON:COMMON.EDIT")}
                    >
                      <$Button
                        size="small"
                        onClick={() => navigateToTextEditor(textObject)}
                        className="ml-1"
                      >
                        <EditOutlined />
                      </$Button>
                    </$Tooltip>
                    <$Tooltip
                      placement="top"
                      title={t(
                        "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.CHANGE_DISPLAY_NAME"
                      )}
                    >
                      <$Button
                        size="small"
                        onClick={() => {
                          setSelectedTextId(textObject?.id);
                          setFieldValue(
                            "displayName",
                            `${textObject.textName}`
                          );
                        }}
                        className="ml-1"
                      >
                        <BoxIcons type={IconTypes.BOX_ICON} name="rename" />
                      </$Button>
                    </$Tooltip>
                    <$Popconfirm
                      title={t(
                        "US.COLLECTION.CASE:MASSAGETEMPLATES.DELETECONFIRM"
                      )}
                      onConfirm={() =>
                        deleteText &&
                        deleteText({
                          textId: textObject.id,
                          textGroup: groupName,
                        })
                      }
                      okText={t("US.COLLECTION.COMMON:COMMON.YES")}
                      cancelText={t("US.COLLECTION.COMMON:COMMON.NO")}
                    >
                      <$Tooltip
                        placement="top"
                        title={t("US.COLLECTION.COMMON:COMMON.DELETE")}
                      >
                        <$Button size="small" className="ml-1">
                          <DeleteOutlined className="text-error" />
                        </$Button>
                      </$Tooltip>
                    </$Popconfirm>
                  </div>
                </div>
              );
            })}
            {textData.length == 0 && !texts.isLoading && (
              <$Empty image={$Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </>
        )}
      </$Skeleton>
      <$Drawer
        title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.TEXT_EDITOR")}
        placement={"right"}
        width={1120}
        onClose={() =>
          setDrawerProperties &&
          setDrawerProperties({
            visibility: false,
            content: INITIAL_TEXT_OBJECT,
          })
        }
        open={drawer?.visibility}
        destroyOnClose
        className="section-editor-drawer"
      >
        <TextEditor
          onClose={() =>
            setDrawerProperties &&
            setDrawerProperties({
              visibility: false,
              content: INITIAL_TEXT_OBJECT,
            })
          }
        />
      </$Drawer>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { editorHome, textEditor, templatesHistory } = state;
  const { texts } = editorHome;
  const { drawer } = textEditor;
  const { templates } = templatesHistory;
  return {
    texts,
    drawer,
    templates,
  };
};

const { editorHome, textEditor } = Actions;
const { texts } = editorHome;
const { drawer, text, textData, adapter } = textEditor;

const mapDispatchToProps = {
  getAllTexts: texts.get,
  setDrawerProperties: drawer.set,
  deleteText: text.delete,
  saveText: textData.update,
  getAdapterId: adapter.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(TemplateList);
